import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PersonalizedServiceImage1 from '../assets/个性化服务1.png';
import ProjectShowcase1 from '../assets/项目展示1.png';
import ProjectShowcase2 from '../assets/项目展示2.png';
import '../styles/personalized-service.css';
import useRevealAnimation from '../hooks/useRevealAnimation';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { appConfig, resolveBackendAssetUrl } from '../config/appConfig';

const API_BASE_URL = appConfig.api.baseUrl;

type SectionItem = {
  title: string;
  description: string;
};

type CaseItem = {
  title: string;
  imageKey?: 'nas' | 'cards';
  imageUrl?: string;
  href?: string;
};

type OneStopSection = {
  label: string;
  title: string;
  processTitle: string;
  processItems: SectionItem[];
  advantageTitle: string;
  advantages: SectionItem[];
};

type CustomPoint = {
  title: string;
  description: string;
};

type CustomItem = {
  index: string;
  title: string;
  points?: CustomPoint[];
  lead?: string;
  paragraphs?: string[];
  caseTitle?: string;
  cases?: CaseItem[];
};

type StrengthSection = {
  label: string;
  title: string;
  intro: string;
  items: SectionItem[];
};

type PlacementItem = {
  title: string;
  slug: string;
  cover_image_url?: string | null;
};

type PlacementResponse = {
  results: Array<{
    slot_key: string;
    item: PlacementItem;
  }>;
};

const SLOT_BY_INDEX: Record<string, string> = {
  '1': 'project-development',
  '2': 'product-incubation',
  '3': 'entrepreneurship-activity',
  '4': 'industry-upgrade',
};

const normalizeLanguage = (language?: string | null): 'zh' | 'en' | 'ja' => {
  const normalized = (language ?? '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('ja')) return 'ja';
  return 'en';
};

const PersonalizedService: React.FC = () => {
  const { t, i18n } = useTranslation();
  const pageRef = useRef<HTMLDivElement>(null);
  const [placementMap, setPlacementMap] = useState<Record<string, PlacementItem>>({});

  useRevealAnimation(pageRef);
  useDocumentTitle(t('PersonalizedService.hero.title'));

  useEffect(() => {
    let cancelled = false;
    const loadPlacements = async () => {
      try {
        const lang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
        const response = await fetch(`${API_BASE_URL}/service-case-placements/?page_key=personalized-service&lang=${lang}`);
        if (!response.ok) return;
        const data = await response.json() as PlacementResponse;
        if (cancelled) return;
        const nextMap = data.results.reduce<Record<string, PlacementItem>>((acc, entry) => {
          acc[entry.slot_key] = entry.item;
          return acc;
        }, {});
        setPlacementMap(nextMap);
      } catch {
        if (!cancelled) setPlacementMap({});
      }
    };

    void loadPlacements();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  const getStructuredValue = <T,>(key: string, fallback: T): T => {
    const value = t(key, { returnObjects: true }) as T | string;
    if (typeof value !== 'string') {
      return value;
    }

    const fallbackValue = t(key, { returnObjects: true, lng: 'zh' }) as T | string;
    return typeof fallbackValue !== 'string' ? fallbackValue : fallback;
  };

  const oneStop = getStructuredValue<OneStopSection>('PersonalizedService.sections.oneStop', {
    label: '',
    title: '',
    processTitle: '',
    processItems: [],
    advantageTitle: '',
    advantages: []
  });
  const custom = getStructuredValue<{ label: string; title: string; items: CustomItem[] }>('PersonalizedService.sections.custom', {
    label: '',
    title: '',
    items: []
  });
  const strengths = getStructuredValue<StrengthSection>('PersonalizedService.sections.strengths', {
    label: '',
    title: '',
    intro: '',
    items: []
  });
  const caseImages: Record<NonNullable<CaseItem['imageKey']>, string> = {
    nas: ProjectShowcase1,
    cards: ProjectShowcase2
  };

  const projectCta = useMemo(() => {
    const lang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    if (lang === 'zh') {
      return {
        label: 'PROJECT COOPERATION',
        title: '\u8fdb\u5165\u9879\u76ee\u5408\u4f5c',
        description: '\u82e5\u4f60\u5e0c\u671b\u56f4\u7ed5\u5b9a\u5236\u5316\u5f00\u53d1\u3001\u4ea7\u54c1\u5b75\u5316\u3001\u521b\u4e1a\u6d3b\u52a8\u6216\u4ea7\u4e1a\u5347\u7ea7\u5f00\u5c55\u5408\u4f5c\uff0c\u53ef\u8fdb\u5165\u9879\u76ee\u5408\u4f5c\u9875\u9762\u67e5\u770b\u8be6\u7ec6\u4fe1\u606f\u3002',
        cta: '\u67e5\u770b\u9879\u76ee\u5408\u4f5c',
      };
    }
    if (lang === 'ja') {
      return {
        label: 'PROJECT COOPERATION',
        title: '\u30d7\u30ed\u30b8\u30a7\u30af\u30c8\u5354\u696d\u3078',
        description: '\u5171\u540c\u958b\u767a\u3001\u30ea\u30bd\u30fc\u30b9\u9023\u643a\u3001\u30b7\u30ca\u30ea\u30aa\u5171\u5275\u3001\u5b9f\u88c5\u63a8\u9032\u306a\u3069\u306e\u5354\u696d\u3092\u3054\u691c\u8a0e\u306e\u5834\u5408\u306f\u3001\u30d7\u30ed\u30b8\u30a7\u30af\u30c8\u5354\u696d\u30da\u30fc\u30b8\u304b\u3089\u3054\u78ba\u8a8d\u304f\u3060\u3055\u3044\u3002',
        cta: '\u30d7\u30ed\u30b8\u30a7\u30af\u30c8\u5354\u696d\u3092\u898b\u308b',
      };
    }
    return {
      label: 'PROJECT COOPERATION',
      title: 'Enter Project Cooperation',
      description: 'If you would like to explore collaboration around joint development, resource coordination, scenario co-creation, or implementation, continue to the Project Cooperation page.',
      cta: 'View Project Cooperation',
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  const customItems = useMemo(() => custom.items.map((item) => {
    const slotKey = SLOT_BY_INDEX[item.index];
    const placement = slotKey ? placementMap[slotKey] : undefined;
    if (!placement) return item;
    return {
      ...item,
      cases: [{
        title: placement.title,
        imageKey: undefined,
        imageUrl: resolveBackendAssetUrl(placement.cover_image_url) ?? undefined,
        href: `/resources/library/${placement.slug}`,
      }],
    } satisfies CustomItem;
  }), [custom.items, placementMap]);

  const stripHeadingPrefix = (value: string) => value.replace(/^\d+(?:\.\d+)?\s*/, '').trim();

  return (
    <div className="personalized-service-page" ref={pageRef}>
      <section className="personalized-service-hero">
        <div className="personalized-service-hero-bg">
          <div className="personalized-service-grid" />
          <div className="personalized-service-glow" />
        </div>
        <div className="section-container personalized-service-hero-content">
          <div className="personalized-service-hero-copy reveal">
            <span className="personalized-service-kicker">{t('PersonalizedService.hero.kicker')}</span>
            <h1 className="personalized-service-hero-title">{t('PersonalizedService.hero.title')}</h1>
            <p className="personalized-service-hero-description">{t('PersonalizedService.hero.description')}</p>
          </div>
        </div>
      </section>

      <section className="section-container personalized-service-section">
        <div className="section-header reveal">
          <span className="section-subtitle">{oneStop.label}</span>
          <h2 className="section-title">{oneStop.title}</h2>
        </div>

        <article className="personalized-service-block reveal delay-1">
          <div className="personalized-service-subsection-head">
            <h3>{stripHeadingPrefix(oneStop.processTitle)}</h3>
          </div>
          <div className="personalized-service-split-layout">
            <div className="personalized-service-process-grid">
              {oneStop.processItems.map((item, index) => {
                const isLastOddItem = oneStop.processItems.length % 2 === 1 && index === oneStop.processItems.length - 1;

                return (
                  <article
                    className={`personalized-service-process-card${isLastOddItem ? ' is-wide' : ''}`}
                    key={item.title}
                  >
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </article>
                );
              })}
            </div>
            <div className="personalized-service-side-visual">
              <img
                src={PersonalizedServiceImage1}
                alt={oneStop.title}
                className="personalized-service-visual-image personalized-service-visual-image-tall"
              />
            </div>
          </div>
        </article>
      </section>

      <section className="section-container personalized-service-section">
        <div className="section-header reveal">
          <span className="section-subtitle">{custom.label}</span>
          <h2 className="section-title">{custom.title}</h2>
        </div>

        <div className="personalized-service-custom-stack">
          {customItems.map((item, index) => {
            const hasSideContent = Boolean(item.cases?.length);
            const isStoryLayout = Boolean(item.paragraphs?.length && !item.points);

            return (
              <article
                className="personalized-service-block reveal"
                key={item.title}
                style={{ transitionDelay: `${0.1 * (index + 1)}s` }}
              >
                <div className="personalized-service-subsection-head">
                  <h3>{stripHeadingPrefix(item.title)}</h3>
                </div>

                <div className={`personalized-service-split-layout personalized-service-split-layout-compact${!hasSideContent ? ' is-text-only' : ''}${isStoryLayout ? ' is-story-layout' : ''}`}>
                  <div className={`personalized-service-content-column${isStoryLayout ? ' is-story' : ''}`}>
                    {item.lead && (
                      isStoryLayout ? (
                        <div className="personalized-service-story-intro">
                          <p className="personalized-service-lead">{item.lead}</p>
                        </div>
                      ) : (
                        <p className="personalized-service-lead">{item.lead}</p>
                      )
                    )}

                    {item.points && (
                      <div className="personalized-service-point-grid">
                        {item.points.map((point) => (
                          <article className="personalized-service-point-card" key={point.title}>
                            <h4>{point.title}</h4>
                            <p>{point.description}</p>
                          </article>
                        ))}
                      </div>
                    )}

                    {item.paragraphs && (
                      <div className={`personalized-service-paragraph-stack${isStoryLayout ? ' is-story-grid' : ''}`}>
                        {item.paragraphs.map((paragraph, paragraphIndex) => (
                          isStoryLayout ? (
                            <article className="personalized-service-story-card" key={paragraphIndex}>
                              <p>{paragraph}</p>
                            </article>
                          ) : (
                            <p key={paragraphIndex}>{paragraph}</p>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                  {item.cases && item.cases.length > 0 ? (
                    <div className="personalized-service-side-visual personalized-service-side-visual-case">
                      <div className="personalized-service-case-panel">
                        {item.caseTitle && (
                          <p className="personalized-service-case-section-title">{item.caseTitle}</p>
                        )}
                        <div className={`personalized-service-case-grid personalized-service-case-grid-side${item.cases.length === 1 ? ' is-single' : ''}`}>
                          {item.cases.map((caseItem) => {
                            const cardContent = (
                              <>
                                <div className="personalized-service-case-image">
                                  {caseItem.imageUrl ? (
                                    <img src={caseItem.imageUrl} alt={caseItem.title} className="personalized-service-visual-image personalized-service-visual-image-case" />
                                  ) : caseItem.imageKey ? (
                                    <img src={caseImages[caseItem.imageKey]} alt={caseItem.title} className="personalized-service-visual-image personalized-service-visual-image-case" />
                                  ) : (
                                    <div className="personalized-service-case-placeholder" aria-hidden="true" />
                                  )}
                                </div>
                                <div className="personalized-service-case-copy">
                                  <span>{caseItem.title}</span>
                                  <span className="personalized-service-case-arrow">-&gt;</span>
                                </div>
                              </>
                            );

                            return caseItem.href ? (
                              <Link className="personalized-service-case-card" key={caseItem.title} to={caseItem.href}>
                                {cardContent}
                              </Link>
                            ) : (
                              <article className="personalized-service-case-card" key={caseItem.title}>
                                {cardContent}
                              </article>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section-container personalized-service-section">
        <div className="section-header reveal">
          <span className="section-subtitle">{strengths.label}</span>
          <h2 className="section-title">{strengths.title}</h2>
        </div>

        <div className="personalized-service-strengths reveal delay-1">
          <p className="personalized-service-strength-intro">{strengths.intro}</p>
          <div className="personalized-service-strength-grid">
            {strengths.items.map((item) => (
              <article className="personalized-service-strength-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section-container personalized-service-section">
        <div className="personalized-service-project-cta reveal">
          <span className="personalized-service-project-kicker">{projectCta.label}</span>
          <h2>{projectCta.title}</h2>
          <p>{projectCta.description}</p>
          <Link to="/contact/projects" className="personalized-service-project-link">
            {projectCta.cta}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PersonalizedService;








